import { maybe, rd } from '@passionware/monads';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { enumFilter } from 'v5/api/_common/query/filters/EnumFilter';
import { searchUtils } from 'v5/api/_common/query/queryUtils';
import { Group } from '../../../../__types__/v1/models/Group';
import { ResourceType } from '../../../../__types__/v1/models/ResourceType';
import Store from '../../../../store';
import { getGroupList } from '../../../../store/v1/groups/groups.selectors';
import { selectResourceTypesList } from '../../../../store/v1/resource_types/resource_types.selectors';
import { GroupsQuery, GroupsService } from './GroupsService';
import { booleanFilter } from 'v5/api/_common/query/filters/BooleanFilter';

function resourceTypeIdToModelMap(
  resourceTypes: ResourceType[]
): Map<number, NonNullable<ResourceType['model']>> {
  const map = new Map<number, NonNullable<ResourceType['model']>>();
  for (const rt of resourceTypes) {
    if (rt.model != null) {
      map.set(rt.id, rt.model);
    }
  }
  return map;
}

function matchesQuery(
  query: GroupsQuery,
  group: Group,
  resourceTypeIdToModel: Map<number, NonNullable<ResourceType['model']>>
): boolean {
  if (!searchUtils.matches(query.search, group.name ?? '')) {
    return false;
  }
  if (!booleanFilter.matches(query.filters.archived, group.archived)) {
    return false;
  }
  if (!enumFilter.matches(query.filters.entity_type, group.entity_type)) {
    return false;
  }
  if (!enumFilter.matches(query.filters.resource_type_id, group.entity_id)) {
    return false;
  }
  if (maybe.isPresent(query.filters.resource_type_model)) {
    const groupModel =
      group.entity_id != null
        ? resourceTypeIdToModel.get(group.entity_id)
        : undefined;
    if (!enumFilter.matches(query.filters.resource_type_model, groupModel)) {
      return false;
    }
  }
  return true;
}

export function createGroupsService(store: typeof Store): GroupsService {
  return {
    useGroups: (query: GroupsQuery) => {
      const groups = useSelector(getGroupList) as Group[];
      const resourceTypes = useSelector(selectResourceTypesList) as
        | ResourceType[]
        | undefined;
      const resourceTypeIdToModel = useMemo(
        () =>
          resourceTypeIdToModelMap(
            Array.isArray(resourceTypes) ? resourceTypes : []
          ),
        [resourceTypes]
      );

      const filteredGroups = useMemo(
        () =>
          groups.filter(group =>
            matchesQuery(query, group, resourceTypeIdToModel)
          ),
        [groups, query, resourceTypeIdToModel]
      );
      return rd.of(filteredGroups);
    },

    useGroup: id => {
      const groups = useSelector(getGroupList) as Group[];

      return useMemo(() => {
        const group = groups.find(g => g.id === id) || null;
        return rd.of(group);
      }, [groups, id]);
    }
  };
}
